import { useState, useEffect, useCallback } from 'react';
import { getSignedUrl, getSignedUrls } from '../lib/signedUrls';

type BucketType = 'photos' | 'recordings' | 'images';

export function useSignedUrl(
  originalUrl: string | null | undefined,
  bucket: BucketType
): { url: string | null; loading: boolean } {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!originalUrl) {
      setSignedUrl(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    getSignedUrl(originalUrl, bucket)
      .then((url) => {
        if (!cancelled) {
          setSignedUrl(url);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSignedUrl(originalUrl);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [originalUrl, bucket]);

  return { url: signedUrl, loading };
}

export function useSignedUrls<T extends { id: string }>(
  items: T[],
  getUrl: (item: T) => string | null | undefined,
  bucket: BucketType
): { urlMap: Map<string, string>; loading: boolean } {
  const [urlMap, setUrlMap] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!items || items.length === 0) {
      setUrlMap(new Map());
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const urlsToFetch = items
      .map((item) => ({
        url: getUrl(item),
        bucket,
      }))
      .filter((item) => item.url);

    if (urlsToFetch.length === 0) {
      setUrlMap(new Map());
      setLoading(false);
      return;
    }

    getSignedUrls(urlsToFetch)
      .then((results) => {
        if (!cancelled) {
          setUrlMap(results);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          const fallbackMap = new Map<string, string>();
          items.forEach((item) => {
            const url = getUrl(item);
            if (url) fallbackMap.set(url, url);
          });
          setUrlMap(fallbackMap);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [items, getUrl, bucket]);

  const getSignedUrlForItem = useCallback(
    (item: T): string | null => {
      const originalUrl = getUrl(item);
      if (!originalUrl) return null;
      return urlMap.get(originalUrl) || originalUrl;
    },
    [urlMap, getUrl]
  );

  return { urlMap, loading };
}
